import { useEffect, useId, useRef, useState } from "react";

// Cloudflare Turnstile widget. The token is verified server-side by Supabase
// (configured in Auth → Settings → CAPTCHA) on auth.signUp /
// resetPasswordForEmail / signInWithPassword via options.captchaToken.

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __turnstileCallbacks?: Record<string, (token: string) => void>;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="turnstile/v0/api.js"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(s);
  });
  return scriptLoadPromise;
}

interface Props {
  siteKey: string;
  onToken: (token: string) => void;
  theme?: "light" | "dark" | "auto";
}

export function Turnstile({ siteKey, onToken, theme = "light" }: Props) {
  const containerId = useId();
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadTurnstileScript()
      .then(() => setReady(true))
      .catch((e) => console.error("Turnstile load failed:", e));
  }, []);

  useEffect(() => {
    if (!ready || !window.turnstile) return;
    const el = document.getElementById(containerId);
    if (!el) return;

    widgetIdRef.current = window.turnstile.render(el, {
      sitekey: siteKey,
      theme,
      callback: (token: string) => onTokenRef.current(token),
      "expired-callback": () => onTokenRef.current(""),
      "error-callback": () => onTokenRef.current(""),
    });

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // siteKey rarely changes; theme too. Re-render on either.
  }, [ready, siteKey, theme, containerId]);

  return <div id={containerId} className="my-1" />;
}
